import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import cs from 'classnames';
import dayjs from 'dayjs';
import { getCookie } from '@lhb/cache';
import { isArray } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { globalExpansionTaskAlias, globalFranchiseeAlias, refactorSelection } from '@/common/utils/ways';
import { employeeListPermission } from '@/common/api/employee';
import { createExpansionTask, taskTypeList, taskSignTypeList } from '@/common/api/expandStore/expansiontask';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';
import { useBigdataFn } from '@/common/utils/bigdata';
import { AssignType } from '@/common/components/business/ExpandStore/ts-config';
import { useTenantInfo } from '@/common/hook/useTenantInfo';

import styles from './index.module.less';
import { Button, Cascader, Col, Form, Row, Space } from 'antd';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Form from '@/common/components/Form/V2Form';
import V2FormProvinceList from '@/common/components/Form/V2FormProvinceList';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import V2FormRadio from '@/common/components/Form/V2FormRadio/V2FormRadio';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import FormFranchisee from './components/FormFranchisee';
import V2Container from '@/common/components/Data/V2Container';

interface TaskCreateDrawerProps {
  showDrawer: boolean; // 是否显示抽屉
  setShowDrawer: Function; // 控制是否显示抽屉
  franchiseeId?: number; // 指定加盟商信息id，可选；指定时不可更改加盟商
  refresh: Function; // 刷新拓店任务表格
}

/** 拓店任务：加盟（益禾堂、非益禾堂）-新增/编辑抽屉
 * @description 拓店任务：加盟（益禾堂、非益禾堂）
 */
const TaskCreateDrawer: FC<TaskCreateDrawerProps> = ({
  showDrawer,
  setShowDrawer,
  franchiseeId,
  refresh,
}) => {

  /* 当前租户信息 */
  const { tenantInfo } = useTenantInfo();

  const [innerMainHeight, setInnerMainHeight] = useState<number>(0);

  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const { globalAliasItemVOs, standardFranchiseeUniqueName } = tenantInfo;
  const franchiseeAlias = globalFranchiseeAlias(globalAliasItemVOs);
  const expansionTaskAlias = globalExpansionTaskAlias(globalAliasItemVOs);
  const [form] = Form.useForm(); // 指派
  const [requesting, setRequesting] = useState(false);
  const [taskTypeOptions, setTaskTypeOptions] = useState<any[]>([]);
  const [signTypeOptions, setSignTypeOptions] = useState<any[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<any>([]); // 指派人选项
  const [targetAdd, setTargetAdd] = useState<any>([]); // 目标城市信息
  const [selFranchiseeId, setSelFranchiseeId] = useState<any>(); // 手动选择的加盟商id
  /** 加盟商详情 */
  const [franchiseeDetail, setFranchiseeDetail] = useState<any>({});
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  const isAssignTypeLeader = [AssignType.Twice, AssignType.ThreeTimes].includes(tenantCheckData?.taskAssignType); // 是否指派主管（二次、三次指派兼容为同样处理）

  /** 加盟商唯一字段名称 */
  const uniqueName = franchiseeDetail?.uniqueName || standardFranchiseeUniqueName || `${franchiseeAlias}唯一标识`;

  useEffect(() => {
    if (showDrawer) {
      methods.getEmployeeOptions();
      methods.getTaskTypeList();
      methods.getSignTypeList();

      // 默认指定加盟商时获取加盟商详情
      if (franchiseeId) {
        methods.getFranchiseeInfo(franchiseeId);
      }
    }
  }, [showDrawer]);

  useEffect(() => {
    methods.onAutoSetName();
  }, [targetAdd]);

  const methods = useMethods({

    /**
     * @description 加盟商详情
     * @param franId
     * @return
     */
    getFranchiseeInfo: (franId: number) => {
      getFranchiseeDetail({ id: franId }).then((data) => {
        const { name, uniqueId } = data;
        form.setFieldsValue({
          franchiseeName: name,
          uniqueId: uniqueId,
        });
        setFranchiseeDetail(data);

        // 刷新自动生成任务名称
        methods.onAutoSetName();
      });
    },

    /** 重置表单，关闭弹窗 */
    onReset: (modal?) => {
      form.resetFields();
      setTargetAdd([]);
      modal && modal.destroy();
      setShowDrawer(false);
    },

    /** 点击取消 */
    onCancel: () => {
      bigdataFn('45e23036-ce94-4c51-9fbd-891378315d27', '创建拓店任务详情-关闭', '点击了创建拓店任务详情-关闭', { op_block: '加盟商管理', op_event: ``, });
      V2Confirm({
        onSure: (modal: any) => methods.onReset(modal),
        content: '取消后将清空内容，请确认是否退出',
      });
    },

    /**
     * @description 获取可指派的团队人员
     */
    getEmployeeOptions: async () => {
      const params = isAssignTypeLeader && isArray(tenantCheckData?.taskAssignRoleIds) ? { roleIds: tenantCheckData?.taskAssignRoleIds } : {};
      const { objectList: data } = await employeeListPermission(params);

      const arr = isArray(data) && data.length ? data.map((item) => ({
        ...item,
        name: `${item.name} ${item.mobile}`
      })) : [];

      setEmployeeOptions(arr);
      if (isAssignTypeLeader && arr.length === 1) { // 指派主管且只有一个时默认指定
        form.setFieldValue('managerId', arr[0].id);
      }
    },

    /**
     * @description 任务类型选项
     */
    getTaskTypeList: () => {
      taskTypeList().then((data) => {
        if (isArray(data) && data.length) {
          const options = data.map((itm: any) => ({ value: itm.id, label: itm.typeName }));
          setTaskTypeOptions(options);
        }
      });
    },
    /**
     * @description 签约类型选项
     */
    getSignTypeList: () => {
      taskSignTypeList().then((data) => {
        if (isArray(data) && data.length) {
          const options = data.map((itm: any) => ({ value: itm.id, label: itm.typeName }));
          setSignTypeOptions(options);
        }
      });
    },

    /** 自动生成名字 */
    onAutoSetName: () => {
      const { franchiseeName, targetAddress } = form.getFieldsValue();

      if (!franchiseeName && !targetAddress) return; // 没有输入的时候不用自动生成

      const infos = [expansionTaskAlias];
      const pcdNames: string[] = [];

      // console.log('addd', targetAddress, targetAdd);
      isArray(targetAdd) && targetAdd.length && pcdNames.push(targetAdd.slice(-1)[0]?.name);
      !!pcdNames.length && infos.unshift(pcdNames.join('、'));

      franchiseeName && infos.unshift(franchiseeName);
      const newName = infos.join('_');

      form.setFieldValue('name', newName);
    },

    handleFranchiseeCreated: (val: any) => {
      setSelFranchiseeId(val);

      // 新增加盟商后获取加盟商详情
      methods.getFranchiseeInfo(val);
    },

    handleFranchisee: (val: any) => {
      setSelFranchiseeId(val);

      // 选择加盟商后获取加盟商详情
      methods.getFranchiseeInfo(val);
    },

    onSubmit: async () => {
      const { radio, assignAccountIds, managerId, ...values } = await form.validateFields();
      bigdataFn('7bfdd966-3a91-44e6-a225-fa4f15c38ddb', '创建拓店任务详情-确定', '点击了创建拓店任务详情-确定', { op_block: '加盟商管理', op_event: ``, });

      let assignAccount: any[] = [];

      if (radio === 1) {
        const accountId = getCookie('employeeId');
        assignAccount = [accountId];
      } else if (radio === 2) {
        if (isAssignTypeLeader) {
          if (!managerId) {
            V2Message.warning('请选择开发主管');
            return;
          }
        } else if (assignAccountIds) {
          assignAccount = [assignAccountIds];
        } else {
          V2Message.warning('请选择指派的团队成员');
          return;
        }
      } else {
        V2Message.warning('请选择指派的成员');
        return;
      }

      try {
        setRequesting(true);
        const {
          name,
          taskTypeId,
          signType,
          expectDropInDate,
          firstArea,
          secondArea,
          targetAddress,
        } = values;

        // 构建目标城市参数
        const targetAddNew = targetAddress && {
          targetProvinceId: targetAddress[0] || undefined,
          targetCityId: targetAddress[1] || undefined,
          targetDistrictId: targetAddress[2] || undefined,
        };
        const params: any = {
          standardFranchiseeId: franchiseeId || selFranchiseeId,
          name,
          taskTypeId,
          signType,
          expectDropInDate: expectDropInDate && dayjs(expectDropInDate).format('YYYY-MM-DD'),
          firstArea,
          secondArea,
          ...targetAddNew,
        };
        // 指派自己、或者指派其他成员时传assignAccountIds，指派主管用managerId
        Object.assign(params, radio === 2 && isAssignTypeLeader ? { managerId } : {
          assignAccountIds: assignAccount,
        });
        createExpansionTask(params)
          .then(() => {
            V2Message.success(`创建${expansionTaskAlias || '拓店任务'}成功`);
            methods.onReset(); // 重置
            refresh();
            setRequesting(false);
          })
          .catch((err) => {
            console.log('catch 失败', err);
            V2Message.error(`创建${expansionTaskAlias || '拓店任务'}失败`);
            setRequesting(false);
          });
      } catch (error) {
        setRequesting(false);
      }
    },

  });

  return (
    <V2Drawer
      className={styles.createTaskDrawer}
      open={showDrawer}
      bodyStyle={{ padding: 0, }}
      onClose={methods.onCancel}
      // contentWrapperStyle={{
      //   minWidth: 'auto',
      //   width: '824px',
      // }}
    >
      <V2Container
        emitMainHeight={(h) => setInnerMainHeight(h)}
        extraContent={{
          top: (<V2Title type='H1' text={`创建${expansionTaskAlias || '拓店任务'}`} className={styles.title} />),
          bottom: (<Space className={styles.bottom} size={12}>
            <Button type='default' onClick={methods.onCancel}>取消</Button>
            <Button type='primary' loading={requesting} onClick={methods.onSubmit}>确定</Button>
          </Space>)
        }}
        style={{ height: '100%', width: '100%' }}
        className={styles.container}>
        <V2Form form={form} className={cs('pt-16 pb-16 pl-40 pr-40', styles.editMain)} style={{ height: innerMainHeight }} >
          <V2Title type='H2' divider text={`${expansionTaskAlias || '拓店任务'}信息`} className={'mb-16'} />
          <Row gutter={24}>
            <Col span={12}>
              <FormFranchisee
                label={`${franchiseeAlias}姓名`}
                placeholder={`请输入${franchiseeAlias}姓名`}
                name='franchiseeName'
                disabled={!!franchiseeId}
                formItemConfig={{
                  rules: [{ required: true, message: `请输入${franchiseeAlias}姓名` }]
                }}
                onCreated={methods.handleFranchiseeCreated}
                onChange={methods.handleFranchisee}
              />
            </Col>
            <Col span={12}>
              <V2FormInput
                required
                label={uniqueName}
                name='uniqueId'
                disabled
              />
            </Col>
            <Col span={12}>
              <V2FormProvinceList
                required
                label='目标城市'
                name='targetAddress'
                config={{
                  changeOnSelect: true,
                  expandTrigger: 'hover',
                  showCheckedStrategy: Cascader.SHOW_PARENT,
                  onChange: (_, selectedOptions) => setTargetAdd(selectedOptions),
                }}
              />
            </Col>
            <Col span={12}>
              <V2FormInput required label={`${expansionTaskAlias || '任务'}名称`} name='name' maxLength={50} />
            </Col>
            <Col span={12}>
              <V2FormSelect
                required
                label={`${expansionTaskAlias || '任务'}类型`}
                name='taskTypeId'
                options={taskTypeOptions} />
            </Col>
            <Col span={12}>
              <V2FormSelect
                required
                label='签约类型'
                name='signType'
                options={signTypeOptions} />
            </Col>
            <Col span={12}>
              <V2FormInput required label='第一意向区域' name='firstArea' maxLength={200} />
            </Col>
            <Col span={12}>
              <V2FormInput required label='第二意向区域' name='secondArea' maxLength={200} />
            </Col>
            <Col span={12}>
              <V2FormDatePicker required label='期望落位日期' name='expectDropInDate' />
            </Col>
          </Row>

          <V2Title type='H2' divider text={`选择${expansionTaskAlias || '拓店'}责任人`} style={{ margin: '16px 0' }} />
          <V2FormRadio
            name='radio'
            className={styles.radioList}
            options={[
              { label: '指派给自己', value: 1 },
              {
                label: isAssignTypeLeader ? (
                  <div className={styles.radioSelect}>
                    指派给开发主管
                    <V2FormSelect
                      className={styles.assignAccountIds}
                      placeholder='请选择开发主管'
                      name='managerId'
                      options={refactorSelection(employeeOptions)}
                      config={{
                        showSearch: true,
                        onClick: e => {
                          e.preventDefault();
                          e.stopPropagation;
                        }, // 解决下拉框闪现的情况
                        onFocus: () => {
                          form.setFieldValue('radio', 2);
                        }, // 下拉框获取焦点的时候单选选择第二个
                      }}
                    />
                  </div>
                ) : (
                  <div className={styles.radioSelect}>
                    指派给团队成员
                    <V2FormSelect
                      className={styles.assignAccountIds}
                      placeholder='请选择指派人'
                      name='assignAccountIds'
                      options={refactorSelection(employeeOptions)}
                      config={{
                        showSearch: true,
                        onClick: e => {
                          e.preventDefault();
                          e.stopPropagation;
                        }, // 解决下拉框闪现的情况
                        onFocus: () => {
                          form.setFieldValue('radio', 2);
                        }, // 下拉框获取焦点的时候单选选择第二个
                      }}
                    />
                  </div>
                ),
                value: 2,
              },
            ]}
          />
        </V2Form>
      </V2Container>
    </V2Drawer>
  );
};
export default TaskCreateDrawer;
